// WebSocket message validation schemas using Zod
// src/lib/types/websocket/schemas.ts

import { z } from 'zod';
import { MessageType } from './messages.types';

// ============================================================================
// BASE SCHEMAS
// ============================================================================

/**
 * Base message schema that all WebSocket messages must conform to
 */
export const BaseMessageSchema = z.object({
	id: z.union([z.string(), z.number()]).optional(),
	timestamp: z.union([z.string(), z.number()]),
	type: z.string(), // Will be refined by specific message schemas
	sequence: z.string().optional(),
	schema_version: z.string().optional()
});

/**
 * Table item schema for data operations
 */
export const TableItemSchema = z.object({
	id: z.union([z.string(), z.number()]),
}).passthrough(); // Allow additional properties

// ============================================================================
// CONNECTION MESSAGES
// ============================================================================

export const ConnectMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.CONNECT),
	clientId: z.string().optional(),
	auth: z.object({
		token: z.string()
	}).optional()
});

export const ConnectionAckMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.CONNECTION_ACK),
	clientId: z.string(),
	status: z.enum(['success', 'error']),
	message: z.string().optional()
});

export const ConnectionStatusMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.CONNECTION_STATUS),
	status: z.enum(['connected', 'error']),
	message: z.string(),
	user_id: z.string().optional(),
	auth_method: z.enum(['jwt', 'api_key']).optional(),
	auto_subscribed: z.array(z.string()).optional(),
	supported_message_types: z.array(z.string()).optional()
});

export const ConnectionEstablishedMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.CONNECTION_ESTABLISHED),
	client_id: z.string()
});

// ============================================================================
// SUBSCRIPTION MESSAGES
// ============================================================================

export const SubscribeMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.SUBSCRIBE),
	tables: z.array(z.string()),
	filters: z.record(z.string(), z.unknown()).optional()
});

export const SubscriptionAckMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.SUBSCRIPTION_ACK),
	tables: z.array(z.string()),
	status: z.enum(['success', 'error']),
	message: z.string().optional()
});

export const SubscriptionStatusMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.SUBSCRIPTION_STATUS),
	status: z.enum(['success', 'error']),
	message: z.string(),
	topics: z.array(z.string()).optional()
});

// ============================================================================
// DATA OPERATION MESSAGES
// ============================================================================

export const DataUpdateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.DATA_UPDATE),
	table: z.string(),
	data: TableItemSchema,
	previousData: TableItemSchema.partial().optional()
});

export const DataInsertMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.DATA_INSERT),
	table: z.string(),
	data: TableItemSchema
});

export const DataDeleteMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.DATA_DELETE),
	table: z.string(),
	id: z.union([z.string(), z.number()])
});

export const BulkUpdateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.BULK_UPDATE),
	table: z.string(),
	data: z.array(TableItemSchema)
});

// ============================================================================
// ERROR MESSAGES
// ============================================================================

export const ErrorMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.ERROR),
	code: z.string(),
	message: z.string(),
	details: z.unknown().optional()
});

export const AuthErrorMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.AUTH_ERROR),
	code: z.string(),
	message: z.string(),
	details: z.unknown().optional()
});

// ============================================================================
// HEARTBEAT & PING/PONG MESSAGES
// ============================================================================

export const HeartbeatMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.HEARTBEAT),
	clientId: z.string()
});

export const PingMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.PING)
});

export const PongMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.PONG)
});

// ============================================================================
// STATUS MESSAGES
// ============================================================================

export const StatusMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.STATUS),
	status: z.enum(['online', 'offline', 'maintenance']),
	message: z.string().optional()
});

export const StatusResponseMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.STATUS_RESPONSE),
	client_id: z.string(),
	user_id: z.string().optional(),
	auth_method: z.enum(['jwt', 'api_key']).optional(),
	subscribed_topics: z.array(z.string()),
	connected: z.boolean(),
	server_time: z.string()
});

// ============================================================================
// UNIFIED DATA_DELTA / DATA_SNAPSHOT SCHEMAS
// ============================================================================
// These unified schemas replace entity-specific schemas for a cleaner architecture.
// - DATA_DELTA: For incremental updates (add, update, remove)
// - DATA_SNAPSHOT: For full data loads (initial load, refresh)
// ============================================================================

/**
 * Unified DATA_DELTA message schema for ALL incremental updates.
 * Routes to the appropriate orchestrator handler based on entity type.
 */
export const DataDeltaMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.DATA_DELTA),
	entity: z.enum(['alerts', 'work_orders', 'approved_alerts', 'notifications']),
	action: z.enum(['add', 'update', 'remove']),
	id: z.string().optional(),
	ids: z.array(z.string()).optional(),
	data: z.record(z.string(), z.unknown()),
	reason: z.string().optional(),
	change_ts: z.string().optional(),
	metadata: z.record(z.string(), z.unknown()).optional()
});

/**
 * Batched DATA_DELTA message schema for bulk operations.
 * Wraps multiple delta items into a single WebSocket message.
 */
export const DataDeltaBatchMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.DATA_DELTA_BATCH),
	items: z.array(z.object({
		entity: z.enum(['alerts', 'work_orders', 'approved_alerts', 'notifications']),
		action: z.enum(['add', 'update', 'remove']),
		id: z.string().optional(),
		ids: z.array(z.string()).optional(),
		data: z.record(z.string(), z.unknown()),
		reason: z.string().optional(),
		change_ts: z.string().optional(),
		metadata: z.record(z.string(), z.unknown()).optional()
	})),
	count: z.number()
});

/**
 * Unified DATA_SNAPSHOT message schema for ALL full data loads.
 * Routes to the appropriate orchestrator handler based on entity type.
 *
 * Data format depends on entity:
 * - alerts: Object with { regular_alerts: [...], approved_alerts: [...] }
 * - others: Array of records
 */
export const DataSnapshotMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.DATA_SNAPSHOT),
	entity: z.enum(['alerts', 'work_orders', 'approved_alerts', 'notifications']),
	// Accept either: array (legacy/other entities) or object (alerts pre-partitioned format)
	data: z.union([
		z.array(z.record(z.string(), z.unknown())),
		z.object({
			regular_alerts: z.array(z.record(z.string(), z.unknown())),
			approved_alerts: z.array(z.record(z.string(), z.unknown()))
		})
	]),
	count: z.number(),
	is_initial: z.boolean().optional(),
	is_partial: z.boolean().optional(),
	groups: z.number().optional(),
	metadata: z.record(z.string(), z.unknown()).optional()
});

// ============================================================================
// REFRESH SIGNAL SCHEMAS (No payload, just triggers a refresh)
// ============================================================================

export const AlertsRefreshRequiredMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.ALERTS_REFRESH_REQUIRED)
});

export const FilteredAlertsUpdateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.FILTERED_ALERTS_UPDATE),
	data: z.array(z.object({
		asset_name: z.string(),
		manager: z.string(),
		distinct_alert_types_count: z.number(),
		alert_details: z.string(),
		all_alert_names: z.string(),
		all_statuses: z.string().optional(),
		all_created_at: z.string().optional(),
		tracker_loss: z.number().optional(),
		fetched_at: z.string().optional()
	})),
	count: z.number().optional(),
	topic: z.string().optional()
});

// ============================================================================
// WORK ORDER REFRESH SIGNALS (No payload, just triggers a refresh)
// ============================================================================

export const WorkOrdersRefreshRequiredMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.WORK_ORDERS_REFRESH_REQUIRED)
});

export const TechWorkOrdersUpdateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.TECH_WORK_ORDERS_UPDATE),
	data: z.array(z.record(z.string(), z.unknown())),
	count: z.number()
});

// ============================================================================
// STATE MESSAGES
// ============================================================================

export const StateUpdateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.STATE_UPDATE),
	namespace: z.string(),
	key: z.string().optional(),
	data: z.record(z.string(), z.unknown()),
	broadcast: z.boolean().optional(),
	ttl: z.number().optional()
});

export const StateUpdateResponseMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.STATE_UPDATE_RESPONSE),
	status: z.enum(['success', 'error']),
	namespace: z.string(),
	key: z.string().optional(),
	data: z.record(z.string(), z.unknown()).optional()
});

export const GetStateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.GET_STATE),
	namespace: z.string(),
	key: z.string().optional()
});

export const StateDataMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.STATE_DATA),
	namespace: z.string(),
	key: z.string().optional(),
	data: z.record(z.string(), z.unknown())
});

// ============================================================================
// USER PRESENCE MESSAGES
// ============================================================================

export const UserPresenceDataSchema = z.object({
	user_id: z.string(),
	display_name: z.string(),
	email: z.string().optional(),
	status: z.enum(['online', 'away', 'busy', 'offline']),
	last_activity: z.string(),
	connection_count: z.number(),
	connections: z.array(z.object({
		client_id: z.string(),
		connected_at: z.string(),
		last_activity: z.string(),
		auth_method: z.enum(['jwt', 'api_key'])
	})).optional()
});

export const GetOnlineUsersMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.GET_ONLINE_USERS),
	include_details: z.boolean().optional()
});

export const OnlineUsersResponseMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.ONLINE_USERS_RESPONSE),
	users: z.array(UserPresenceDataSchema),
	total_count: z.number()
});

export const OnlineUsersMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.ONLINE_USERS),
	users: z.array(UserPresenceDataSchema),
	total_count: z.number()
});

export const GetUserStatusMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.GET_USER_STATUS),
	user_id: z.string()
});

export const UserStatusResponseMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.USER_STATUS_RESPONSE),
	user_id: z.string(),
	display_name: z.string().optional(),
	email: z.string().optional(),
	status: z.enum(['online', 'away', 'busy', 'offline']),
	last_activity: z.string(),
	connection_count: z.number(),
	connections: z.array(z.object({
		client_id: z.string(),
		connected_at: z.string(),
		last_activity: z.string(),
		auth_method: z.enum(['jwt', 'api_key'])
	})).optional()
});

export const UpdateUserPresenceMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.UPDATE_USER_PRESENCE),
	status: z.enum(['online', 'away', 'busy', 'offline'])
});

export const PresenceUpdateSuccessMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.PRESENCE_UPDATE_SUCCESS),
	user_id: z.string(),
	status: z.enum(['online', 'away', 'busy', 'offline'])
});

export const SystemPresenceUpdateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.SYSTEM_PRESENCE_UPDATE),
	user_id: z.string(),
	display_name: z.string(),
	email: z.string().optional(),
	status: z.enum(['online', 'away', 'busy', 'offline']),
	last_activity: z.string(),
	connection_count: z.number(),
	event_type: z.enum(['user_connected', 'user_disconnected', 'status_changed'])
});

export const ViewerStateUpdateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.VIEWER_STATE_UPDATE),
	// Backend sends viewer updates as: { type, data: { itemId, userId, userName, action, ... } }
	data: z.object({
		itemId: z.string(),
		userId: z.string(),
		userName: z.string(),
		userEmail: z.string().optional(),
		action: z.enum(['start_viewing', 'stop_viewing']),
		timestamp: z.string()
	})
});

export const GetPresenceStatsMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.GET_PRESENCE_STATS)
});

export const PresenceStatsResponseMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.PRESENCE_STATS_RESPONSE),
	stats: z.object({
		total_users: z.number(),
		online_users: z.number(),
		away_users: z.number(),
		busy_users: z.number(),
		offline_users: z.number(),
		total_connections: z.number()
	})
});

// ============================================================================
// UPDATE MESSAGES
// ============================================================================

export const UpdateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.UPDATE),
	data: z.union([
		// User action update
		z.object({
			update_id: z.string().optional(),
			update_type: z.enum(['user_action', 'system_event', 'notification', 'presence', 'data_sync', 'presence_heartbeat', 'presence_request']),
			entity_type: z.string().optional(),
			entity_id: z.union([z.string(), z.number()]).optional(),
			changes: z.record(z.string(), z.object({
				old: z.unknown(),
				new: z.unknown()
			})).optional(),
			source_user_id: z.string().optional(),
			timestamp: z.string().optional()
		}),
		// State update
		z.object({
			update_type: z.literal('state_update'),
			namespace: z.string(),
			key: z.string().optional(),
			data: z.unknown(),
			timestamp: z.string().optional()
		})
	])
});

export const UpdateResponseMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.UPDATE_RESPONSE),
	status: z.enum(['broadcasted', 'failed']),
	recipients: z.number(),
	update_id: z.string()
});

// ============================================================================
// INITIAL STATE MESSAGES
// ============================================================================

export const InitialStateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.INITIAL_STATE),
	users: z.record(z.string(), z.object({
		user_id: z.string(),
		client_id: z.string(),
		status: z.enum(['online', 'offline']),
		auth_method: z.enum(['jwt', 'api_key']),
		connected_at: z.string(),
		last_activity: z.string(),
		display_name: z.string().optional(),
		connection_count: z.number().optional()
	})),
	presence_data: z.object({
		online_users: z.array(UserPresenceDataSchema),
		total_online: z.number(),
		stats: z.object({
			total_users: z.number(),
			online_users: z.number(),
			away_users: z.number(),
			busy_users: z.number(),
			offline_users: z.number(),
			total_connections: z.number()
		})
	}).optional()
});

export const InitialDataCompleteMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.INITIAL_DATA_COMPLETE),
	loaded: z.array(z.string())
});

// ============================================================================
// REQUEST MESSAGES
// ============================================================================

export const RequestDataMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.REQUEST_DATA),
	dataType: z.enum(['alerts', 'work_orders', 'all']),
	reason: z.string().optional()
});

export const DataRequestResponseMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.DATA_REQUEST_RESPONSE),
	status: z.enum(['success', 'error']),
	message: z.string(),
	data_type: z.string(),
	reason: z.string().optional()
});

// ============================================================================
// NOTIFICATION MESSAGES
// ============================================================================

export const NewNotificationMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.NEW_NOTIFICATION),
	notification: z.object({
		id: z.string(),
		title: z.string(),
		message: z.string(),
		type: z.string(),
		status: z.enum(['UNREAD', 'READ']),
		scope: z.enum(['user', 'org', 'global']),
		created_at: z.string(),
		action_url: z.string().optional(),
		is_read: z.boolean()
	})
});

export const NotificationCountUpdateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.NOTIFICATION_COUNT_UPDATE),
	unread_count: z.number()
});

export const NotificationsUpdateMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.NOTIFICATIONS_UPDATE),
	data: z.array(z.record(z.string(), z.unknown())),
	count: z.number(),
	unread_count: z.number(),
	is_initial: z.boolean().optional()
});

export const NotificationEventMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.NOTIFICATION),
	data: z.record(z.string(), z.unknown()),
	unread_count: z.number().optional()
});

export const NotificationReadResponseMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.NOTIFICATION_READ_RESPONSE),
	status: z.enum(['success', 'error']),
	message: z.string(),
	notification_id: z.string().optional()
});

// ============================================================================
// CHAT MESSAGES
// ============================================================================

export const ChatMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.CHAT_MESSAGE),
	event: z.enum(['new_message', 'message_updated', 'message_deleted']),
	room_id: z.string(),
	message: z.object({
		id: z.string(),
		content: z.string(),
		sender_id: z.string(),
		sender_name: z.string(),
		message_type: z.enum(['text', 'file', 'image']),
		is_read: z.boolean()
	})
});

export const ChatRoomJoinedMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.CHAT_ROOM_JOINED),
	status: z.enum(['success', 'error']),
	message: z.string(),
	room_id: z.string().optional(),
	subscribed_topic: z.string().optional()
});

export const ChatMessageResponseSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.CHAT_MESSAGE_RESPONSE),
	status: z.enum(['success', 'error']),
	message: z.string(),
	message_id: z.string().optional(),
	room_id: z.string().optional()
});

// ============================================================================
// TASK PROGRESS MESSAGES
// ============================================================================

export const TaskProgressMessageSchema = BaseMessageSchema.extend({
	type: z.literal(MessageType.TASK_PROGRESS),
	task_id: z.string(),
	task_type: z.string(),
	stage: z.string(),
	status: z.string(),
	progress: z.number(),
	message: z.string(),
	metadata: z.record(z.string(), z.unknown()).optional()
});

// ============================================================================
// MASTER SCHEMA MAP
// ============================================================================

/**
 * Map of message types to their validation schemas
 * Used for runtime validation of incoming WebSocket messages
 */
export const MESSAGE_SCHEMAS = {
	// Connection
	[MessageType.CONNECT]: ConnectMessageSchema,
	[MessageType.CONNECTION_ACK]: ConnectionAckMessageSchema,
	[MessageType.CONNECTION_STATUS]: ConnectionStatusMessageSchema,
	[MessageType.CONNECTION_ESTABLISHED]: ConnectionEstablishedMessageSchema,

	// Subscription
	[MessageType.SUBSCRIBE]: SubscribeMessageSchema,
	[MessageType.SUBSCRIPTION_ACK]: SubscriptionAckMessageSchema,
	[MessageType.SUBSCRIPTION_STATUS]: SubscriptionStatusMessageSchema,

	// Data Operations
	[MessageType.DATA_UPDATE]: DataUpdateMessageSchema,
	[MessageType.DATA_INSERT]: DataInsertMessageSchema,
	[MessageType.DATA_DELETE]: DataDeleteMessageSchema,
	[MessageType.BULK_UPDATE]: BulkUpdateMessageSchema,

	// Errors
	[MessageType.ERROR]: ErrorMessageSchema,
	[MessageType.AUTH_ERROR]: AuthErrorMessageSchema,

	// Heartbeat
	[MessageType.HEARTBEAT]: HeartbeatMessageSchema,
	[MessageType.PING]: PingMessageSchema,
	[MessageType.PONG]: PongMessageSchema,

	// Status
	[MessageType.STATUS]: StatusMessageSchema,
	[MessageType.STATUS_RESPONSE]: StatusResponseMessageSchema,

	// Unified DATA_DELTA / DATA_SNAPSHOT (replaces entity-specific message schemas)
	[MessageType.DATA_DELTA]: DataDeltaMessageSchema,
	[MessageType.DATA_DELTA_BATCH]: DataDeltaBatchMessageSchema,
	[MessageType.DATA_SNAPSHOT]: DataSnapshotMessageSchema,

	// Refresh signals and misc alerts/work order messages
	[MessageType.ALERTS_REFRESH_REQUIRED]: AlertsRefreshRequiredMessageSchema,
	[MessageType.FILTERED_ALERTS_UPDATE]: FilteredAlertsUpdateMessageSchema,
	[MessageType.WORK_ORDERS_REFRESH_REQUIRED]: WorkOrdersRefreshRequiredMessageSchema,
	[MessageType.TECH_WORK_ORDERS_UPDATE]: TechWorkOrdersUpdateMessageSchema,

	// State
	[MessageType.STATE_UPDATE]: StateUpdateMessageSchema,
	[MessageType.STATE_UPDATE_RESPONSE]: StateUpdateResponseMessageSchema,
	[MessageType.GET_STATE]: GetStateMessageSchema,
	[MessageType.STATE_DATA]: StateDataMessageSchema,

	// User Presence
	[MessageType.GET_ONLINE_USERS]: GetOnlineUsersMessageSchema,
	[MessageType.ONLINE_USERS_RESPONSE]: OnlineUsersResponseMessageSchema,
	[MessageType.ONLINE_USERS]: OnlineUsersMessageSchema,
	[MessageType.GET_USER_STATUS]: GetUserStatusMessageSchema,
	[MessageType.USER_STATUS_RESPONSE]: UserStatusResponseMessageSchema,
	[MessageType.UPDATE_USER_PRESENCE]: UpdateUserPresenceMessageSchema,
	[MessageType.PRESENCE_UPDATE_SUCCESS]: PresenceUpdateSuccessMessageSchema,
	[MessageType.SYSTEM_PRESENCE_UPDATE]: SystemPresenceUpdateMessageSchema,
	[MessageType.VIEWER_STATE_UPDATE]: ViewerStateUpdateMessageSchema,
	[MessageType.GET_PRESENCE_STATS]: GetPresenceStatsMessageSchema,
	[MessageType.PRESENCE_STATS_RESPONSE]: PresenceStatsResponseMessageSchema,

	// Updates
	[MessageType.UPDATE]: UpdateMessageSchema,
	[MessageType.UPDATE_RESPONSE]: UpdateResponseMessageSchema,

	// Initial State
	[MessageType.INITIAL_STATE]: InitialStateMessageSchema,
	[MessageType.INITIAL_DATA_COMPLETE]: InitialDataCompleteMessageSchema,

	// Requests
	[MessageType.REQUEST_DATA]: RequestDataMessageSchema,
	[MessageType.DATA_REQUEST_RESPONSE]: DataRequestResponseMessageSchema,

	// Notifications
	[MessageType.NEW_NOTIFICATION]: NewNotificationMessageSchema,
	[MessageType.NOTIFICATION_COUNT_UPDATE]: NotificationCountUpdateMessageSchema,
	[MessageType.NOTIFICATIONS_UPDATE]: NotificationsUpdateMessageSchema,
	[MessageType.NOTIFICATION]: NotificationEventMessageSchema,
	[MessageType.NOTIFICATION_READ_RESPONSE]: NotificationReadResponseMessageSchema,

	// Chat
	[MessageType.CHAT_MESSAGE]: ChatMessageSchema,
	[MessageType.CHAT_ROOM_JOINED]: ChatRoomJoinedMessageSchema,
	[MessageType.CHAT_MESSAGE_RESPONSE]: ChatMessageResponseSchema,

	// Task Progress
	[MessageType.TASK_PROGRESS]: TaskProgressMessageSchema
} as const;

/**
 * Generic schema for messages without specific validation
 * Falls back to basic structure validation
 */
export const GenericMessageSchema = BaseMessageSchema.passthrough();

/**
 * Validate an incoming WebSocket message
 * @param message - Raw message object
 * @returns Validated message or throws ZodError
 */
export function validateMessage(message: unknown): z.infer<typeof GenericMessageSchema> {
	// First, ensure it's a valid base message
	const baseMessage = BaseMessageSchema.parse(message);

	// Try to find specific schema for message type
	const schema = MESSAGE_SCHEMAS[baseMessage.type as keyof typeof MESSAGE_SCHEMAS];

	if (schema) {
		// Validate against specific schema
		return schema.parse(message);
	}

	// Fall back to generic validation for unknown message types
	return GenericMessageSchema.parse(message);
}

/**
 * Safe validation that returns a tuple [data, error]
 * @param message - Raw message object
 * @returns Tuple of [validatedMessage | null, error | null]
 */
export function safeValidateMessage(
	message: unknown
): [z.infer<typeof GenericMessageSchema> | null, z.ZodError | null] {
	try {
		const validated = validateMessage(message);
		return [validated, null];
	} catch (error) {
		if (error instanceof z.ZodError) {
			return [null, error];
		}
		throw error; // Re-throw unexpected errors
	}
}

/**
 * Check if a message conforms to expected schema (no throw)
 * @param message - Raw message object
 * @returns True if valid, false otherwise
 */
export function isValidMessage(message: unknown): boolean {
	try {
		validateMessage(message);
		return true;
	} catch {
		return false;
	}
}
